/**
 * PATH: apps/quote/src/components/builder/builder-workspace/storage.ts
 * PURPOSE: LocalStorage persistence helpers for workspace preferences
 */

import type { QuoteDetail } from '@/lib/quoteApi'
import type { ActiveSheet } from './types'

export const STORAGE_KEYS = {
  leftPanelOpen: 'quotech:builder:leftPanelOpen',
  leftPanelWidth: 'quotech:builder:leftPanelWidth',
  rightDrawerWidth: 'quotech:builder:rightDrawerWidth',
  rightDrawerPinned: 'quotech:builder:rightDrawerPinned',
  rightDrawerTab: 'quotech:builder:rightDrawerTab',
  showKeyboardHints: 'quotech:builder:showKeyboardHints',
  gridView: 'quotech:builder:gridView',
  activeSheetPrefix: 'quotech:builder:activeSheet:',
}

export function loadFromStorage<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue
  try {
    const stored = localStorage.getItem(key)
    if (stored === null) return defaultValue
    return JSON.parse(stored) as T
  } catch {
    return defaultValue
  }
}

export function saveToStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Ignore storage errors
  }
}

export function getDefaultActiveSheet(quote: QuoteDetail | null): ActiveSheet {
  const firstSectionId = quote?.sections?.[0]?.id || null
  return { type: 'section', sectionId: firstSectionId }
}

export function loadActiveSheetForQuote(quoteId: string | undefined, quote: QuoteDetail | null): ActiveSheet {
  if (!quoteId) return getDefaultActiveSheet(quote)
  const key = `${STORAGE_KEYS.activeSheetPrefix}${quoteId}`
  const stored = loadFromStorage<ActiveSheet | null>(key, null)
  if (!stored) return getDefaultActiveSheet(quote)
  if (stored.type === 'section' && stored.sectionId) {
    const sectionExists = quote?.sections?.some(s => s.id === stored.sectionId)
    if (!sectionExists) return getDefaultActiveSheet(quote)
  }
  return stored
}

export function saveActiveSheetForQuote(quoteId: string | undefined, sheet: ActiveSheet): void {
  if (!quoteId) return
  const key = `${STORAGE_KEYS.activeSheetPrefix}${quoteId}`
  saveToStorage(key, sheet)
}
